import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { INTERNAL_LINKS, STATUS_LIST } from '../../../enum';
import { cleanQueryParams, toQueryParams } from '../../../utils/queryParams';
import HeroCard from './HeroCard';
import classNames from './styles.module.scss';
import UpcomingDeadlines from './UpcomingDeadlines';
import WhatHappensNext from './WhatHappensNext';

// ─── Date parse formats ───────────────────────────────────────────────────────

const DATE_FORMATS = ['MM/DD/YYYY H:mm:ss', 'MM/DD/YYYY HH:mm:ss', moment.ISO_8601];

// ─── Hero state derivation ────────────────────────────────────────────────────

const getHeroState = (currentPeriod, driverStatus, hasNextPeriodVariableReceipt) => {
  if (!currentPeriod?.dateOfTripSubmissionDeadline) return null;

  const deadline = moment(currentPeriod.dateOfTripSubmissionDeadline, DATE_FORMATS);
  if (!deadline.isValid()) return null;

  const daysUntilDeadline = deadline.startOf('day').diff(moment().startOf('day'), 'days');

  if (driverStatus === 'approved') {
    const paymentRaw = currentPeriod.achPaymentDate || currentPeriod.paymentDate;
    const paymentDate = paymentRaw ? moment(paymentRaw, DATE_FORMATS) : null;
    const daysUntil = paymentDate?.isValid()
      ? paymentDate.startOf('day').diff(moment().startOf('day'), 'days')
      : null;
    return { state: 'approved', daysUntil, deadline };
  }

  if (driverStatus === 'submitted') {
    return { state: 'submitted', daysUntil: daysUntilDeadline, deadline };
  }

  if (daysUntilDeadline < 0) {
    if (hasNextPeriodVariableReceipt) return null;
    return { state: 'overdue', daysUntil: daysUntilDeadline, deadline };
  }
  if (daysUntilDeadline === 0)
    return { state: 'due-today', daysUntil: daysUntilDeadline, deadline };
  if (daysUntilDeadline > 7) return { state: 'far-out', daysUntil: daysUntilDeadline, deadline };
  return { state: 'on-track', daysUntil: daysUntilDeadline, deadline };
};

// ─── Deadline group classification ───────────────────────────────────────────

const CURRENT_PERIOD_DATE_KEYS = [
  'dateOfTripSubmissionDeadline',
  'dateOfManagerApprovalDeadline',
  'paymentDate',
  'achPaymentDate',
];

const getPeriodBounds = currentPeriod => {
  let start = null;
  let end = null;

  if (!currentPeriod) return { start, end };

  CURRENT_PERIOD_DATE_KEYS.forEach(key => {
    if (!currentPeriod[key]) return;
    const d = moment(currentPeriod[key], DATE_FORMATS).startOf('day');
    if (!d.isValid()) return;
    if (!start || d.isBefore(start)) start = d;
    if (!end || d.isAfter(end)) end = d;
  });

  return { start, end };
};

const toSortedGroups = bucket =>
  Object.values(bucket).sort((a, b) => (a.monthKey < b.monthKey ? -1 : 1));

const classifyDeadlineGroups = (deadlineGroups, currentPeriod, nextPeriod) => {
  if (!deadlineGroups.length) return { previous: [], current: [], next: [] };

  const { start: periodStart, end: periodEnd } = getPeriodBounds(currentPeriod);
  const { start: nextStart, end: nextEnd } = getPeriodBounds(nextPeriod);

  const previousBucket = {};
  const currentBucket = {};
  const nextBucket = {};

  const addToGroup = (bucket, item, d) => {
    const monthKey = d.format('YYYY-MM');
    if (!bucket[monthKey]) {
      bucket[monthKey] = { monthKey, monthLabel: d.format('MMMM YYYY'), items: [] };
    }
    bucket[monthKey].items.push(item);
  };

  deadlineGroups.forEach(group => {
    group.items.forEach(item => {
      const d = moment(item.date, 'MMM D, YYYY').startOf('day');
      if (!d.isValid()) return;

      if (periodStart && periodEnd) {
        if (d.isBefore(periodStart)) {
          addToGroup(previousBucket, item, d);
        } else if (d.isAfter(periodEnd)) {
          if (nextStart && nextEnd && d.isSameOrAfter(nextStart) && d.isSameOrBefore(nextEnd)) {
            addToGroup(nextBucket, item, d);
          }
        } else {
          addToGroup(currentBucket, item, d);
        }
      } else {
        // No current period info — treat all as current
        addToGroup(currentBucket, item, d);
      }
    });
  });

  return {
    previous: toSortedGroups(previousBucket),
    current: toSortedGroups(currentBucket),
    next: toSortedGroups(nextBucket),
  };
};

// ─── Period range label ───────────────────────────────────────────────────────

const getPeriodRange = currentPeriod => {
  if (!currentPeriod) return null;

  const submission = currentPeriod.dateOfTripSubmissionDeadline
    ? moment(currentPeriod.dateOfTripSubmissionDeadline, DATE_FORMATS)
    : null;
  const payment =
    currentPeriod.achPaymentDate || currentPeriod.paymentDate
      ? moment(currentPeriod.achPaymentDate || currentPeriod.paymentDate, DATE_FORMATS)
      : null;

  if (submission?.isValid() && payment?.isValid()) {
    return `${submission.format('MMM D')} – ${payment.format('MMM D')}`;
  }
  if (payment?.isValid()) {
    return payment.format('MMM YYYY');
  }
  return null;
};

// ─── Main component ───────────────────────────────────────────────────────────

const DriverDeadlineView = ({
  t,
  deadlineGroups,
  currentPeriod,
  nextPeriod,
  dashboardData,
  driverStatus,
}) => {
  const heroInfo = useMemo(
    () => getHeroState(currentPeriod, driverStatus, dashboardData?.hasNextPeriodVariableReceipt),
    [currentPeriod, driverStatus, dashboardData],
  );
  const { previous, current, next } = useMemo(
    () => classifyDeadlineGroups(deadlineGroups, currentPeriod, nextPeriod),
    [deadlineGroups, currentPeriod, nextPeriod],
  );

  const periodRange = useMemo(() => getPeriodRange(currentPeriod), [currentPeriod]);

  const approvalFormatted = useMemo(() => {
    if (!currentPeriod?.dateOfManagerApprovalDeadline) return null;
    const d = moment(currentPeriod.dateOfManagerApprovalDeadline, DATE_FORMATS);
    return d.isValid() ? d.format('MMM D') : null;
  }, [currentPeriod]);

  const paymentFormatted = useMemo(() => {
    const raw = currentPeriod?.achPaymentDate || currentPeriod?.paymentDate;
    if (!raw) return null;
    const d = moment(raw, DATE_FORMATS);
    return d.isValid() ? d.format('MMM D, YYYY') : null;
  }, [currentPeriod]);

  const submittedDateFormatted = useMemo(() => {
    if (!dashboardData?.submittedDate) return null;
    const d = moment(dashboardData.submittedDate, DATE_FORMATS);
    return d.isValid() ? d.format('MMM D, YYYY') : null;
  }, [dashboardData]);

  const approvedDateFormatted = useMemo(() => {
    if (!dashboardData?.approvedDate) return null;
    const d = moment(dashboardData.approvedDate, DATE_FORMATS);
    return d.isValid() ? d.format('MMM D, YYYY') : null;
  }, [dashboardData]);

  const history = useHistory();

  // Same date range logic as the "Trips Recorded" card in CardsRowSection:
  // startDateRange = oldest pending trip timestamp from the dashboard data API response.
  // endDateRange   = submission deadline (bounds the range to the current period).
  const tripsDateRange = useMemo(() => {
    const start = dashboardData?.oldestJourneyStartTs || undefined;
    const end = currentPeriod?.dateOfTripSubmissionDeadline
      ? moment(currentPeriod.dateOfTripSubmissionDeadline, DATE_FORMATS).toISOString()
      : undefined;
    return { start, end };
  }, [dashboardData, currentPeriod]);

  // All trips for the current period, regardless of status.
  const handleViewTrips = useCallback(() => {
    history.push({
      pathname: INTERNAL_LINKS.TRIPS,
      search: toQueryParams({
        startDateRange: tripsDateRange.start,
        endDateRange: tripsDateRange.end,
        status: '',
      }),
    });
  }, [history, tripsDateRange]);

  const handleViewSubmittedTrips = useCallback(() => {
    history.push({
      pathname: INTERNAL_LINKS.TRIPS,
      search: cleanQueryParams({
        startDateRange: tripsDateRange.start,
        endDateRange: tripsDateRange.end,
        status: STATUS_LIST().Status.SUBMITTED,
      }),
    });
  }, [history, tripsDateRange]);

  const handleSubmitTrips = useCallback(() => {
    history.push({
      pathname: INTERNAL_LINKS.TRIPS,
      search: cleanQueryParams({
        startDateRange: tripsDateRange.start,
        endDateRange: tripsDateRange.end,
        status: STATUS_LIST().Status.PENDING,
      }),
    });
  }, [history, tripsDateRange]);

  return (
    <div className={classNames.wrapper}>
      {/* ── Section heading ── */}
      <div className={classNames.sectionHeader}>
        <span className={classNames.sectionTitleLg}>{t('yourDeadlines')}</span>
        {periodRange && (
          <span className={classNames.periodBadge}>
            {periodRange} · {t('currentPeriod')}
          </span>
        )}
      </div>

      {/* ── Hero card ── */}
      {heroInfo && (
        <HeroCard
          t={t}
          state={heroInfo.state}
          daysUntil={heroInfo.daysUntil}
          deadlineFormatted={heroInfo.deadline.format('MMMM D')}
          approvalFormatted={approvalFormatted}
          paymentFormatted={paymentFormatted}
          periodRange={periodRange}
          submittedDateFormatted={submittedDateFormatted}
          approvedDateFormatted={approvedDateFormatted}
          onSubmitTrips={handleSubmitTrips}
          onViewTrips={handleViewTrips}
          onViewSubmittedTrips={handleViewSubmittedTrips}
        />
      )}

      {/* ── What Happens Next ── */}
      {currentPeriod && (
        <WhatHappensNext
          t={t}
          currentPeriod={currentPeriod}
          heroState={heroInfo?.state || 'far-out'}
          onSubmitTrips={handleSubmitTrips}
        />
      )}

      {/* ── Upcoming Deadlines ── */}
      <div className={classNames.upcomingSection}>
        <div className={classNames.upcomingSectionHeader}>
          <span className={classNames.sectionTitle}>{t('upcomingDeadlines')}</span>
        </div>
        <UpcomingDeadlines t={t} previous={previous} current={current} next={next} />
      </div>

      {/* ── Legend ── */}
      <div className={classNames.legend}>
        <span className={classNames.legendTitle}>{t('statusKey')}</span>
        <span className={classNames.legendItem}>
          <span className={classNames.dot} style={{ background: '#DC2626' }} />
          {t('dueTodayOrOverdue')}
        </span>
        <span className={classNames.legendItem}>
          <span className={classNames.dot} style={{ background: '#FBBF24' }} />
          {t('dueWithin3Days')}
        </span>
        <span className={classNames.legendItem}>
          <span className={classNames.dot} style={{ background: '#14B8A6' }} />
          {t('onTrackDays')}
        </span>
        <span className={classNames.legendItem}>
          <span className={classNames.dot} style={{ background: '#16A34A' }} />
          {t('completed')}
        </span>
        <span className={classNames.legendItem}>
          <span className={classNames.dot} style={{ background: '#CBD5E1' }} />
          {t('upcomingOrPassed')}
        </span>
      </div>
    </div>
  );
};

export default DriverDeadlineView;
